/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { SKELETON_BLOCK, skeletonSlots } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';

const CARD = 'rounded-(--radius-lg) border-(--border-subtle) bg-(--surface-primary) border p-(--space-5)';

/**
 * Placeholder for the settings sidebar nav (group label + nav rows).
 * @returns A shimmering sidebar-shaped placeholder.
 */
function SidebarSkeleton(): React.ReactElement {
  return (
    <div
      className="flex flex-col gap-(--space-3) rounded-(--radius-lg) border-(--border-subtle) bg-(--surface-primary) border p-(--space-4)"
      style={{ width: 240 }}
    >
      {skeletonSlots(5).map((slot) => (
        <div key={slot} className={`${SKELETON_BLOCK} h-7 w-full`} />
      ))}
    </div>
  );
}

/**
 * Placeholder for a single settings form card (heading + paired fields).
 * @returns A shimmering card-shaped placeholder.
 */
function CardSkeleton(): React.ReactElement {
  return (
    <div className={`flex flex-col gap-(--space-4) ${CARD}`}>
      <div className={`${SKELETON_BLOCK} h-5 w-1/3`} />
      <div className="grid grid-cols-2 gap-(--space-4)">
        {skeletonSlots(6).map((slot) => (
          <div key={slot} className="flex flex-col gap-(--space-2)">
            <div className={`${SKELETON_BLOCK} h-3 w-1/4`} />
            <div className={`${SKELETON_BLOCK} h-4 w-2/3`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Full-page skeleton mirroring the settings layout (header bar, left nav and the
 * profile section cards) shown while the agency data is still loading.
 *
 * @returns A shimmering settings-shaped placeholder.
 */
export const SettingsLoadingSkeleton: React.FC = () => (
  <div data-testid="settings-loading" className="flex flex-col gap-(--space-5) p-(--space-5)">
    <div className={`flex items-center justify-between ${CARD}`}>
      <div className={`${SKELETON_BLOCK} h-6 w-48`} />
      <div className={`${SKELETON_BLOCK} h-5 w-24`} />
    </div>
    <div className="grid items-start gap-(--space-5)" style={{ gridTemplateColumns: '240px 1fr' }}>
      <SidebarSkeleton />
      <div className="flex flex-col gap-(--space-4)">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    </div>
  </div>
);
